import React, { useState } from 'react';
import { X, Check, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { api, friendlyError } from '../lib/api';
import { FormError, FormBanner } from './FormError';
import useFormValidation from '../hooks/useFormValidation';

export default function ContactAgentModal({ agent, property, onClose }) {
  const { toast } = useToast();
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: `Hola ${agent.name.split(' ')[0]}, estoy interesado en la propiedad "${property.title}". ¿Podrías brindarme más información?`,
  });
  const v = useFormValidation();

  const set = (k, val) => { setForm((f) => ({ ...f, [k]: val })); v.clearField(k); };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    v.reset();
    const ok = v.validate({
      name: !form.name || form.name.trim().length < 2 ? 'El nombre debe tener al menos 2 caracteres.' : null,
      email: !form.email ? 'Email es obligatorio.'
        : !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email) ? 'Email no tiene un formato válido.'
        : null,
      phone: !form.phone || form.phone.trim().length < 7 ? 'Ingresa un teléfono válido.' : null,
      message: !form.message || form.message.trim().length < 10 ? 'El mensaje debe tener al menos 10 caracteres.' : null,
    });
    if (!ok) return;
    setSubmitting(true);
    try {
      await api.sendMessage({
        property_id: property.id,
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
      });
      setSent(true);
    } catch (err) {
      const first = v.applyError(err, 'Revisa tus datos e intenta nuevamente.');
      if (!first) {
        toast({
          title: 'No pudimos enviar tu mensaje',
          description: friendlyError(err, 'Revisa tus datos e intenta nuevamente.'),
          variant: 'destructive',
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-serif text-xl font-bold text-habita-dark flex items-center gap-2"><Mail className="w-5 h-5" /> Contactar agente</h2>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center" aria-label="Cerrar"><X className="w-5 h-5" /></button>
        </div>
        {sent ? (
          <div className="p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-habita-mint mx-auto flex items-center justify-center mb-4"><Check className="w-8 h-8 text-habita" /></div>
            <h3 className="font-serif text-xl font-bold text-habita-dark">Mensaje enviado</h3>
            <p className="text-sm text-gray-600 mt-2">{agent.name} te contactará muy pronto.</p>
            <Button onClick={onClose} className="btn-habita mt-5 px-8 h-11 rounded-full">Cerrar</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4" noValidate>
            <FormBanner msg={v.banner} testid="contact-error-banner" />
            <div className="flex items-center gap-3 bg-habita-mint rounded-xl p-3">
              <img src={agent.avatar} alt="" className="w-12 h-12 rounded-full object-cover" />
              <div>
                <p className="font-semibold text-sm text-habita-dark">{agent.name}</p>
                <p className="text-xs text-gray-600">{agent.agency}</p>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Tu nombre</Label>
              <Input
                value={form.name}
                onChange={e => set('name', e.target.value)}
                className={`mt-1.5 h-11 ${v.errorClass('name')}`}
                data-testid="contact-name-input"
                {...v.field('name')}
              />
              <FormError msg={v.fieldErrors.name} testid="contact-error-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={e => set('email', e.target.value)}
                className={`mt-1.5 h-11 ${v.errorClass('email')}`}
                data-testid="contact-email-input"
                {...v.field('email')}
              />
              <FormError msg={v.fieldErrors.email} testid="contact-error-email" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Teléfono</Label>
              <Input
                type="tel"
                value={form.phone}
                onChange={e => set('phone', e.target.value)}
                className={`mt-1.5 h-11 ${v.errorClass('phone')}`}
                data-testid="contact-phone-input"
                {...v.field('phone')}
              />
              <FormError msg={v.fieldErrors.phone} testid="contact-error-phone" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Mensaje</Label>
              <textarea
                rows={4}
                value={form.message}
                onChange={e => set('message', e.target.value)}
                className={`mt-1.5 w-full rounded-md border bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 ${v.fieldErrors.message ? 'border-rose-500 focus:ring-rose-400' : 'border-input focus:ring-habita'}`}
                data-testid="contact-message-input"
                {...v.field('message')}
              />
              <FormError msg={v.fieldErrors.message} testid="contact-error-message" />
            </div>
            <Button type="submit" disabled={submitting} className="btn-habita w-full h-12 rounded-full disabled:opacity-60" data-testid="contact-submit-btn">
              {submitting ? 'Enviando...' : 'Enviar mensaje'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
